import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Grid,
    Typography,
    CircularProgress,
    IconButton,
    useMediaQuery,
    useTheme as useMuiTheme
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { createBranch, updateBranch, fetchBranchCities } from '../../store/slices/branchSlice';
import { useTheme } from '../../contexts/ThemeContext';

// Branch type options
const BRANCH_TYPES = [
    { value: 'Express Center', label: 'Express Center' },
    { value: 'Shop N Shop (SNS)', label: 'Shop N Shop (SNS)' },
    { value: 'Franchise', label: 'Franchise' },
    { value: 'Main Branch', label: 'Main Branch' }
];

interface BranchFormProps {
    open: boolean;
    onClose: () => void;
    onSuccess?: (isEdit: boolean, branchName: string) => void;
    onError?: (message: string) => void;
    branch?: any;
    mode: 'create' | 'edit';
}

const BranchForm: React.FC<BranchFormProps> = ({ open, onClose, onSuccess, onError, branch, mode }) => {
    const { theme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
    const dispatch = useAppDispatch();
    const { allCities, loading } = useAppSelector((state) => state.branches);

    const [formData, setFormData] = useState({
        name: '',
        code: '',
        city_id: '',
        type: 'Main Branch',
        status: 'active'
    });

    const [errors, setErrors] = useState<Record<string, string>>({});

    // Helper to convert API status (boolean/number) to form status (string)
    const getStatusString = (status: any): string => {
        if (typeof status === 'boolean') {
            return status ? 'active' : 'inactive';
        }
        if (typeof status === 'number') {
            return status === 1 ? 'active' : 'inactive';
        }
        return status === 'active' ? 'active' : 'inactive';
    };

    useEffect(() => {
        if (open) {
            dispatch(fetchBranchCities());
            if (mode === 'edit' && branch) {
                setFormData({
                    name: branch.name || '',
                    code: branch.code || '',
                    city_id: branch.city_id ? branch.city_id.toString() : '',
                    type: branch.type || 'Main Branch',
                    status: getStatusString(branch.status)
                });
            } else {
                setFormData({
                    name: '',
                    code: '',
                    city_id: '',
                    type: 'Main Branch',
                    status: 'active'
                });
            }
            setErrors({});
        }
    }, [open, mode, branch, dispatch]);

    const handleInputChange = (field: string, value: string | number) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));
        
        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};

        if (!formData.name.trim()) {
            newErrors.name = 'Branch name is required';
        }

        if (!formData.code.trim()) {
            newErrors.code = 'Branch code is required';
        }

        if (!formData.type.trim()) {
            newErrors.type = 'Branch type is required';
        }

        if (!formData.status.trim()) {
            newErrors.status = 'Branch status is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            const branchData: any = {
                name: formData.name,
                code: formData.code,
                type: formData.type,
                status: formData.status === 'active' ? 1 : 0
            };

            // Add city_id only if provided
            if (formData.city_id) {
                branchData.city_id = parseInt(formData.city_id, 10);
            }

            if (mode === 'create') {
                await dispatch(createBranch(branchData)).unwrap();
                onSuccess?.(false, formData.name);
            } else {
                await dispatch(updateBranch({ id: branch.id, branchData })).unwrap();
                onSuccess?.(true, formData.name);
            }
            onClose();
        } catch (error: any) {
            console.error('Error saving branch:', error);
            onError?.(error?.message || 'An error occurred while saving the branch');
        }
    };

    const handleClose = () => {
        if (!loading) {
            onClose();
        }
    };

    return (
        <Dialog 
            open={open} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            fullScreen={isMobile}
            PaperProps={{
                sx: {
                    borderRadius: isMobile ? 0 : 2,
                    minHeight: isMobile ? '100vh' : 'auto'
                }
            }}
        >
            <form onSubmit={(e) => { e.preventDefault(); handleSubmit(); }}>
                <DialogTitle sx={{ 
                    p: isMobile ? 2 : 3, 
                    mb:2,
                    pb: isMobile ? 1 : 2,
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    borderBottom: `1px solid ${theme.palette.divider}`
                }}>
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {mode === 'create' ? 'Add New Branch' : 'Edit Branch'}
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        disabled={loading}
                        sx={{ minWidth: 'auto', p: 1 }}
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: isMobile ? 2 : 3, pt: 3 }}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Branch Name *"
                                value={formData.name}
                                onChange={(e) => handleInputChange('name', e.target.value)}
                                error={!!errors.name}
                                helperText={errors.name}
                                disabled={loading}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                fullWidth
                                label="Branch Code *"
                                value={formData.code}
                                onChange={(e) => handleInputChange('code', e.target.value.toUpperCase())}
                                error={!!errors.code}
                                helperText={errors.code}
                                disabled={loading}
                                required
                                sx={{
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: 3,
                                        '& fieldset': {
                                            borderWidth: '1px',
                                        },
                                    },
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth error={!!errors.type} required>
                                <InputLabel>Type *</InputLabel>
                                <Select
                                    value={formData.type}
                                    label="Type *"
                                    onChange={(e) => handleInputChange('type', e.target.value)}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    {BRANCH_TYPES.map((type) => (
                                        <MenuItem key={type.value} value={type.value}>
                                            {type.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.type && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {errors.type}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth error={!!errors.status} required>
                                <InputLabel>Status *</InputLabel>
                                <Select
                                    value={formData.status}
                                    label="Status *"
                                    onChange={(e) => handleInputChange('status', e.target.value)}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="inactive">Inactive</MenuItem>
                                </Select>
                                {errors.status && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {errors.status}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth error={!!errors.city_id}>
                                <InputLabel>City (Optional)</InputLabel>
                                <Select
                                    value={formData.city_id}
                                    label="City (Optional)"
                                    onChange={(e) => handleInputChange('city_id', e.target.value)}
                                    disabled={loading}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            borderRadius: 3,
                                            '& fieldset': {
                                                borderWidth: '1px',
                                            },
                                        },
                                    }}
                                >
                                    <MenuItem value="">Select City</MenuItem>
                                    {allCities.map((city) => (
                                        <MenuItem key={city.id} value={city.id}>
                                            {city.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                                {errors.city_id && (
                                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.5 }}>
                                        {errors.city_id}
                                    </Typography>
                                )}
                            </FormControl>
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{ 
                    p: isMobile ? 2 : 3, 
                    borderTop: `1px solid ${theme.palette.divider}`,
                    gap: 1,
                    flexDirection: isMobile ? 'column' : 'row'
                }}>
                    <Button
                        onClick={handleClose}
                        disabled={loading}
                        fullWidth={isMobile}
                        sx={{ 
                            color: theme.palette.text.secondary,
                            borderRadius: 3
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        fullWidth={isMobile}
                        sx={{
                            bgcolor: theme.palette.secondary.main,
                            borderRadius: 3,
                            '&:hover': {
                                bgcolor: theme.palette.secondary.dark,
                            }
                        }}
                    >
                        {loading ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            mode === 'create' ? 'Create Branch' : 'Update Branch'
                        )}
                    </Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default BranchForm;
